import { chatApis } from '@/configs/apis/chat';
import { useSessionContext } from '@/contexts/session/context';
import { MyError, MyEventTarget } from '@/scripts/extends';
import { useRefs } from '@/scripts/hooks';
import type { RetryOptions } from '@/scripts/utils/queue';
import Queue, { retry } from '@/scripts/utils/queue';
import { pushLog } from '@/scripts/utils/devUtils';
import {
  resMap,
  reqMap,
  ChatSocketControls,
  SendMessageResponseStatusCode,
  FlagValue,
} from './types';
import type { ChatSocketRes, ResMap, ReqMap, ChatSocketReq, Conditions } from './types';

export function useChatSocket() {
  const {
    session: { user },
  } = useSessionContext();
  const [reconnectCount, setReconnectCount] = useState(0);
  const [socket, setSocket] = useState<WebSocket>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const sendIdRef = useRef(0);
  const sendHandlerRef = useRef<Record<string, (res: ChatSocketRes) => void>>({});
  const sessionId = useMemo(() => {
    const sessionId = Math.random().toString();
    sessionStorage.setItem('sessionId', sessionId);
    return sessionId;
  }, []);
  const event = useMemo(() => {
    const o = new MyEventTarget<{
      message: ChatSocketRes;
    }>();

    function onMessage(
      handler: (res: ChatSocketRes) => ResolveValue<any>,
      conditions?: Conditions,
      options?: {
        queue: boolean;
      }
    ) {
      const { queue: needQueue } = Object.assign({ queue: false }, options);

      let queue: Queue | undefined;
      if (needQueue) {
        queue = new Queue();
      }

      function isPass(res: ChatSocketRes) {
        return (
          !conditions ||
          Object.keys(conditions).every((key: string) => {
            if (key === 'control') {
              return conditions[key as keyof Conditions] === res.headers[key];
            }
            return conditions[key as keyof Conditions] === res.body[key];
          })
        );
      }

      return o.addEventListener('message', (e) => {
        const res = e.detail!;
        if (isPass(res)) {
          if (queue) {
            queue.push(() => handler(res));
          } else {
            handler(res);
          }
        }
      });
    }

    return Object.assign(o, { onMessage });
  }, []);

  const refs = useRefs({ socket, event, sessionId });

  const connect = useCallback(() => {
    const { socket: oldSocket, event, sessionId } = refs.current;

    oldSocket?.close();

    setSocket(undefined);
    setLoading(true);
    setError(undefined);
    sendIdRef.current = 0;
    sendHandlerRef.current = {};

    const socket = chatApis.getSocket();

    if (socket) {
      socket.addEventListener('open', () => {
        pushLog(`socket:open`);
        setLoading(false);
        setSocket(socket);
        setReconnectCount(0);
      });

      socket.addEventListener('error', (e) => {
        pushLog('socket:error', e);
        setError(new Error('Connect error.'));
      });

      socket.addEventListener('close', () => {
        pushLog('socket:close', sessionId);
        if (sessionId === sessionStorage.getItem('sessionId')) {
          setLoading(false);
          setSocket(undefined);
          setReconnectCount((count) => count + 1);
        }
      });

      socket.addEventListener('message', async (e) => {
        const data: Blob = e.data;
        const ab = await data.arrayBuffer();
        const headerAb = ab.slice(0, 4);
        const payloadAb = ab.slice(headerAb.byteLength);
        const [control, flags, id1, id2] = Array.from(new Uint8Array(headerAb));
        const protobuf = resMap[control as keyof ResMap];

        const retry = (flags >> 7) & 1;
        const once = (flags >> 6) & 1;

        const res: ChatSocketRes = {
          headers: {
            control,
            flags: { retry, once },
          },
          body: protobuf.decode(new Uint8Array(payloadAb)),
        };

        const id = (id1 << 8) | id2;
        const handler = sendHandlerRef.current[id];
        if (handler) {
          handler(res);
          delete sendHandlerRef.current[id];
        } else {
          pushLog(`socket:notice:${ChatSocketControls[control]}_${id}`, res);
        }
        event.dispatchEvent('message', res);
      });
    }

    return socket;
  }, [refs]);

  const send = useCallback<
    <C extends keyof ReqMap>(
      req: ChatSocketReq<C>,
      retryOptions?: RetryOptions
    ) => Promise<ChatSocketRes>
  >(
    ({ headers: { control, flags }, body }, retryOptions) => {
      return retry(
        (byRetry) =>
          new Promise<ChatSocketRes>((resolve, reject) => {
            const { socket } = refs.current;
            if (socket?.readyState === WebSocket.OPEN) {
              const id = (sendIdRef.current++ % 65534) + 1;
              const id1 = id >> 8;
              const id2 = id & 0xff;

              const retry =
                (flags?.retry ?? (byRetry ? FlagValue.YES : FlagValue.NO)) << 7;
              const once = (flags?.once ?? FlagValue.NO) << 6;

              const flags_ = retry | once;

              const headerArray = [control, flags_, id1, id2];

              const protobuf = reqMap[control];
              const bodyU8A = protobuf.encode(body as any);
              const messageU8A = new Uint8Array(headerArray.length + bodyU8A?.length);

              messageU8A.set(headerArray);
              messageU8A.set(bodyU8A, headerArray.length);

              const cb = pushLog('socket:' + ChatSocketControls[control] + '_' + id, {
                headers: {
                  control,
                  flags,
                },
                body,
              });

              socket.send(messageU8A);

              sendHandlerRef.current[id] = (res) => {
                cb?.(res);
                const statusCode = res.body.statusCode;
                if (
                  statusCode !== undefined &&
                  statusCode !== SendMessageResponseStatusCode.SUCCESS
                ) {
                  reject(
                    new ChatSocketError(
                      {
                        message: SendMessageResponseStatusCode[statusCode],
                        code: statusCode,
                      },
                      res
                    )
                  );
                } else {
                  resolve(res);
                }
              };
            } else {
              reject(new Error('Socket not connected.'));
            }
          }),
        retryOptions
      );
    },
    [refs]
  );

  const equalityErrorCode = useCallback(
    (error: any, code: SendMessageResponseStatusCode) => {
      return error instanceof ChatSocketError ? error.code === code : false;
    },
    []
  );

  useLayoutEffect(() => {
    if (user?.userId) {
      connect();
    }
  }, [connect, user?.userId]);

  const value = useMemo(
    () => ({
      socket,
      loading,
      setLoading,
      error,
      setError,
      connect,
      send,
      event,
      reconnectCount,
      equalityErrorCode,
    }),
    [connect, error, event, loading, send, socket, reconnectCount, equalityErrorCode]
  );

  return value;
}

export class ChatSocketError extends MyError<SendMessageResponseStatusCode> {}
